import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OrderApiService } from '@features/order/services/order-api/order-api.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AuthFacade } from '@rd/core/auth/store/facades/auth.facade';
import { ShoppingCartPhysicalDeliveryFormData } from '@rd/form-builder/types/shopping-cart-delivery-form-data.type';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { CheckoutErrorDialogComponent } from '@rd/shared-legacy/components/checkout-error-dialog/checkout-error-dialog.component';
import { HttpError } from '@rd/types/http-error.type';
import { Order } from '@rd/types/order.type';
import { User } from '@rd/types/user.type';
import { isNotNull } from '@rd/utils/isNotNull';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import * as actions from '../actions/delivery-details.actions';
import { OrderCheckoutFacade } from '../facades/order-checkout.facade';
import { UpdateOrderApiAdapter } from '../services/update-order-api.adapter';

@Injectable()
export class DeliveryDetailsEffects {
  constructor(
    private orderCheckoutFacade: OrderCheckoutFacade,
    private actions$: Actions,
    private router: Router,
    private orderApiService: OrderApiService,
    private dialogService: DialogService,
    private updateOrderAdapter: UpdateOrderApiAdapter,
    private authFacade: AuthFacade,
  ) {}

  updateDeliveryDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.DeliveryDetailsActionTypes.UPDATE_DELIVERY_DETAILS),
      withLatestFrom(
        this.orderCheckoutFacade.currentOrder$.pipe(filter(isNotNull)),
        this.authFacade.user$.pipe(filter(isNotNull)),
      ),
      switchMap(
        ([{ deliveryDetails }, { id }, { email }]: [
          { deliveryDetails: ShoppingCartPhysicalDeliveryFormData },
          Order, // Just the id is needed here
          User,
        ]) => {
          // The logic is quite complex/needs an adapter to separate the converting the form data to api params
          const params = this.updateOrderAdapter.convert({
            id,
            formData: deliveryDetails,
            email,
          });

          return this.orderApiService.update(params).pipe(
            map((order: Order) => {
              return actions.UpdateDeliveryDetailsSuccess({
                order,
              });
            }),
            catchError((error: HttpError) =>
              of(actions.UpdateDeliveryDetailsFail(error)),
            ),
          );
        },
      ),
    ),
  );

  updateDeliveryDetailsFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.DeliveryDetailsActionTypes.UPDATE_DELIVERY_DETAILS_FAIL),
        tap((error: HttpError) => {
          this.dialogService.open(CheckoutErrorDialogComponent, {
            data: { error, iconName: 'unexpected-error' },
            id: 'sc-checkout-error-dialog',
            screenMode: 'center-md',
          });
        }),
      ),
    { dispatch: false },
  );
}
